























import Base from '@/views/Base';
import { Component, Prop } from 'vue-property-decorator';
import WSwitch from '@/components/WSwitch.vue';
import { CreateSurveyElement, SurveyAnswerOption, SurveyElement } from '@/models/Presentation';
import SpontaneousSurveySettings from '@/views/presentation/components/SpontaneousSurveySettings.vue';
import PresentationService from '@/service/PresentationService';
import { Validations } from 'vuelidate-property-decorators';
import { required } from 'vuelidate/lib/validators';

@Component({
  components: { SpontaneousSurveySettings, WSwitch }
})
export default class AddSurveyModal extends Base {
  @Prop({ default: null }) editItem!: SurveyElement | null;

  editingSurvey = false;

  survey: CreateSurveyElement = {
    question: '',
    description: '',
    surveyElementType: 'SINGLE_CHOICE',
    answers: [],
    chartType: 'PIE_CHART',
    chartNumberType: 'ABSOLUTE',
    liveEvaluation: false,
    colorScheme: [],
    correctAnswers: false
  };

  @Validations()
  validations = {
    survey: {
      question: { required },
      description: {},
      surveyElementType: {},
      answers: {
        required: (answers: SurveyAnswerOption[]) => answers.some(a => a.text.trim() !== '')
      },
      chartType: {},
      chartNumberType: {}
    }
  };

  surveyBackground: { file: string | null, url: string | null } = { file: null, url: null };

  shown(): void {
    if (this.editItem) {
      this.survey = {
        question: this.editItem.question,
        description: this.editItem.description,
        surveyElementType: this.editItem.surveyElementType,
        answers: this.editItem.answers.map((a, idx) => ({ ...a, idx })),
        chartType: this.editItem.chartType,
        chartNumberType: this.editItem.chartNumberType,
        liveEvaluation: this.editItem.liveEvaluation,
        colorScheme: this.editItem.colorScheme,
        backgroundFilename: this.editItem.backgroundFilename,
        backgroundChanged: false,
        correctAnswers: this.editItem.correctAnswers
      };

      if (this.editItem.backgroundFilename && this.editItem.presentationId) {
        this.surveyBackground.url = PresentationService.getSurveyBackgroundUrl(this.editItem.presentationId, this.editItem.id, this.editItem.backgroundFilename, this.user.resourceAccessToken);
      }

      this.editingSurvey = true;
    } else {
      this.reset();
    }
  }

  show() {
    this.$v.$reset();
  }

  submit(ok: () => void): void {
    this.$v.$touch();
    if (this.$v.$invalid) {
      return;
    }

    this.survey.answers = this.survey.answers
      .filter(a => a.text.trim() !== '')
      .map((a, idx) => {
        a.idx = idx;
        a.correct = this.survey.correctAnswers && a.correct;
        return a;
      });
    if (this.editingSurvey) {
      this.$emit('edit-spontaneous-survey', this.survey, this.editItem?.id, this.surveyBackground.file);
    } else {
      this.$emit('add-spontaneous-survey', this.survey, this.surveyBackground.file);
    }

    ok();
  }

  hidden(): void {
    this.$emit('hidden');
    this.reset();
  }

  colorSchemeOptions: { value: string[], text: string }[] = [
    {
      value: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9e480e', '#636363', '#997300', '#255e91', '#43682b', '#698ed0', '#f1975a', '#b7b7b7', '#ffcd33', '#7cafdd', '#8cc168', '#335aa1', '#d26012'],
      text: 'UF'
    },
    {
      value: ['#001219', '#005F73', '#0A9396', '#94D2BD', '#E9D8A6', '#EE9B00', '#CA6702', '#BB3E03', '#AE2012', '#9B2226'],
      text: ''
    },
    {
      value: ['#e66000', '#ff9501', '#ffca00', '#00539f', '#0195dd', '#321e53', '#002147'],
      text: ''
    },
    {
      value: ['#0085c2', '#7ab800', '#f2af00', '#dc5134', '#ce1127', '#b7295b', '#6f2586', '#71c6c1', '#5582ab', '#009bba', '#444444', '#f1f2f2', '#eeeeee'],
      text: ''
    },
    {
      value: ['#005387', '#8db811', '#fdb813', '#88aca2', '#000000', '#798cb6', '#a1a1a3', '#8f908b'],
      text: ''
    },
    {
      value: ['#050e2b', '#003665', '#00aeff', '#3369e7', '#8e43e6', '#b84592', '#ff4f80', '#ff6b5f', '#fec168', '#2dde98', '#1cc7cf'],
      text: ''
    },
    {
      value: ['#0c1c2c', '#3a82e4', '#3fb2a3', '#eff1ec', '#dedacf', '#d0e6da', '#085855', '#23366e', '#143d4f', '#7d245c', '#d25237', '#edca4a'],
      text: ''
    },
    {
      value: ['#5fcaf8', '#fdcb2e', '#fd9526', '#fb3157', '#157efb', '#53d769', '#8f8e93'],
      text: ''
    },
    {
      value: ['#3bb2d0', '#3887bf', '#8a8aca', '#55b881', '#50667e', '#41afa6', '#fa886d', '#e45e5d', '#ed6498', '#fbb03b', '#142736'],
      text: ''
    },
    {
      value: ['#1429a0', '#74787b', '#0689d9', '#ffc500', '#ff6900', '#e5002b', '#e5002b', '#c800a1', '#685bc7', '#0057b8', '#00a9e0', '#009ca5', '#00b13f'],
      text: ''
    },
    {
      value: ['#0079be', '#70b500', '#ff9f1a', '#eb5a47', '#f1d600', '#c378e1', '#ff78cc', '#00c1e0', '#51e897', '#c4c9cc'],
      text: ''
    },
    {
      value: ['#264653', '#2A9D8F', '#E9C46A', '#F4A261', '#E76F51', '#D62828', '#023047', '#8ECAE6'],
      text: 'Mediterranean'
    },
    {
      value: ['#000000', '#1C1C1C', '#36454F', '#555555', '#696969', '#888888', '#A9A9A9', '#B0B0B0', '#C0C0C0', '#D3D3D3'], // 10 Farben
      text: 'Minimalist Monochrome'
    }
  ];

  reset(): void {
    this.survey = {
      question: '',
      description: '',
      surveyElementType: 'SINGLE_CHOICE',
      answers: [
        { text: '', correct: false, idx: 0, color: this.colorSchemeOptions[0].value[0] },
        { text: '', correct: false, idx: 0, color: this.colorSchemeOptions[0].value[1] },
        { text: '', correct: false, idx: 0, color: this.colorSchemeOptions[0].value[2] },
        { text: '', correct: false, idx: 0, color: this.colorSchemeOptions[0].value[3] }
      ],
      chartType: 'BAR_CHART_V',
      chartNumberType: 'ABSOLUTE',
      liveEvaluation: false,
      colorScheme: this.colorSchemeOptions[0].value,
      correctAnswers: false
    };
    this.editingSurvey = false;
    this.surveyBackground.file = null;
    this.surveyBackground.url = null;
  }

  get isSubmitDisabled(): boolean {
    return false;
  }

}
